import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MediaGrid from '@/components/feature/MediaGrid';
import PlayerArea from '@/pages/detail/components/PlayerArea';
import EpisodeSelect from '@/pages/detail/components/EpisodeSelect';
import DetailActions from '@/pages/detail/components/DetailActions';
import { useSeo } from '@/hooks/useSeo';
import { getMediaById, getRelatedMedia } from '@/utils/media';
import { MEDIA_TYPE_LABEL } from '@/types/media';

export default function DetailPage() {
  const { id = '' } = useParams();
  const item = getMediaById(id);
  const [episode, setEpisode] = useState(1);

  useSeo({
    title: item ? `${item.title} - 黄色影视` : '影视详情 - 黄色影视',
    description: item
      ? `${item.title}：${item.summary} 主演：${item.cast.join('、')}，导演：${item.director}。`
      : '黄色影视影视详情页。',
    keywords: item ? `${item.title},${item.genres.join(',')},黄色影视,在线观看` : '黄色影视',
  });

  useEffect(() => {
    setEpisode(1);
  }, [id]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-2xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
          <p className="mt-3 text-sm text-foreground-600">你访问的影视资源不存在或已下架，去首页看看其他内容吧。</p>
          <Link
            to="/"
            className="mt-6 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMedia(item.id, 6);
  const episodeLabel = item.episodes ? `第 ${episode} 集` : undefined;

  const scrollToPlayer = () => {
    document.getElementById('player')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <SiteLayout>
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={item.backdrop || item.cover}
            alt={`${item.title} 剧照`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/90 to-background-50/40"></div>
        </div>

        <div className="relative mx-auto max-w-[1400px] px-4 pb-8 pt-8 md:px-6 md:pb-10 md:pt-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-500">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${item.type}`} className="cursor-pointer transition-colors hover:text-primary-500">
              {MEDIA_TYPE_LABEL[item.type]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{item.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                  {MEDIA_TYPE_LABEL[item.type]}
                </span>
                {item.badge ? (
                  <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                    {item.badge}
                  </span>
                ) : null}
                <span className="flex items-center gap-1 text-sm font-semibold text-primary-500">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
                {item.title}
              </h1>

              <p className="mt-3 text-sm text-foreground-600 md:text-base">
                {item.year} · {item.region} · {item.genres.join(' / ')} · {item.duration}
              </p>

              <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-foreground-700">
                {item.summary}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={scrollToPlayer}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg"></i>
                立即播放
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 pt-8 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div id="player" className="scroll-mt-24">
              <PlayerArea title={item.title} poster={item.backdrop || item.cover} episodeLabel={episodeLabel} />
            </div>

            {item.episodes ? (
              <div className="mt-6">
                <EpisodeSelect total={item.episodes} current={episode} onChange={setEpisode} />
              </div>
            ) : null}

            <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">剧情简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{item.summary}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                《{item.title}》是一部{item.year}年的{item.region}
                {MEDIA_TYPE_LABEL[item.type]}，由{item.director}执导，
                {item.cast.slice(0, 3).join('、')}等主演。影片融合{item.genres.join('、')}元素，
                在上线后获得了广泛关注。你可以通过下方的相关推荐，发现更多同类型作品。
              </p>
            </div>

            <div className="mt-8">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500/15 text-primary-500">
                  <i className="ri-magic-line text-lg"></i>
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
              </div>
              <MediaGrid items={related} className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5" />
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="overflow-hidden rounded-lg border border-background-200/70">
                <div className="aspect-[2/3] w-full bg-background-100">
                  <img
                    src={item.cover}
                    alt={`${item.title} 海报`}
                    title={`${item.title} - 黄色影视`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <DetailActions mediaId={item.id} title={item.title} />

              <div className="rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">详细信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">主演</dt>
                    <dd className="text-foreground-800">{item.cast.join(' / ')}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">导演</dt>
                    <dd className="text-foreground-800">{item.director}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">类型</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {item.genres.map((genre) => (
                        <span key={genre} className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                          {genre}
                        </span>
                      ))}
                    </dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">地区</dt>
                    <dd className="text-foreground-800">{item.region}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">年份</dt>
                    <dd className="text-foreground-800">{item.year}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">时长</dt>
                    <dd className="text-foreground-800">{item.duration}</dd>
                  </div>
                  {item.episodes ? (
                    <div className="flex gap-3">
                      <dt className="w-12 shrink-0 text-foreground-500">集数</dt>
                      <dd className="text-foreground-800">共 {item.episodes} 集</dd>
                    </div>
                  ) : null}
                  <div className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">更新</dt>
                    <dd className="text-foreground-800">{item.updatedAt}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">分类入口</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to="/category/movie"
                    className="cursor-pointer rounded-md bg-background-200/70 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    电影
                  </Link>
                  <Link
                    to="/category/tv"
                    className="cursor-pointer rounded-md bg-background-200/70 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    电视剧
                  </Link>
                  <Link
                    to="/category/variety"
                    className="cursor-pointer rounded-md bg-background-200/70 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    综艺
                  </Link>
                  <Link
                    to={`/search?q=${encodeURIComponent(item.cast[0] ?? item.title)}`}
                    className="cursor-pointer rounded-md bg-background-200/70 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    更多《{item.cast[0]}》作品
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}